/**
 * TenantActionsToolbar — pasek 4 akcji adminskich na tenancie (F8 T8.4a).
 *
 * Akcje:
 *  - Zawieś   → POST /api/admin/tenants/:id/suspend       (kaskada: active/trialing → suspended)
 *  - Wznów    → POST /api/admin/tenants/:id/activate      (kaskada: suspended → active)
 *  - Usuń (miękko) → POST /api/admin/tenants/:id/soft-delete  (gcrecord = NOW + reason 5-500)
 *  - Przywróć → POST /api/admin/tenants/:id/restore       (gcrecord = NULL)
 *
 * Disabled states (UX layer; backend dodatkowo waliduje 409):
 *  - Zawieś:   tenant skasowany OR brak subskrypcji active/trialing
 *  - Wznów:    tenant skasowany OR brak subskrypcji suspended
 *  - Usuń:     tenant już skasowany
 *  - Przywróć: tenant nie jest skasowany
 *
 * Każdy klik otwiera confirm modal. Po sukcesie → onSuccess (parent invalidate query).
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';
import { Database } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

import { ImpersonateModal } from './ImpersonateModal';

interface Subscription {
  status: string | null;
}

export interface TenantActionsToolbarProps {
  tenantId: string;
  tenantName: string;
  isSoftDeleted: boolean;
  /** Baza tenanta jeszcze nie istnieje (czeka na serwer klienta / zakładanie) — nie ma do czego się logować ani czego przeglądać. */
  dbUnavailable?: boolean;
  subscriptions: Subscription[];
  onSuccess: () => void;
}

type Dialog = null | 'suspend' | 'activate' | 'softDelete' | 'restore' | 'impersonate';

export function TenantActionsToolbar({
  tenantId,
  tenantName,
  isSoftDeleted,
  dbUnavailable = false,
  subscriptions,
  onSuccess,
}: TenantActionsToolbarProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [dialog, setDialog] = useState<Dialog>(null);
  // Przeglądarka bazy (tylko odczyt) — osobne uprawnienie ITE.
  const canBrowseDb = hasPermission('tenants.db_browse');

  const hasActive = subscriptions.some((s) => s.status === 'active' || s.status === 'trialing');
  const hasSuspended = subscriptions.some((s) => s.status === 'suspended');

  const close = () => setDialog(null);
  const handleSuccess = () => {
    setDialog(null);
    onSuccess();
  };

  return (
    <div
      className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-border-secondary bg-bg-secondary px-3 py-2"
      role="toolbar"
      aria-label={t('admin.tenants.actions.toolbarLabel')}
    >
      <Button
        variant="secondary"
        size="sm"
        disabled={isSoftDeleted || !hasActive}
        onClick={() => setDialog('suspend')}
        title={
          isSoftDeleted
            ? t('admin.tenants.actions.suspend.disabledDeleted')
            : !hasActive
              ? t('admin.tenants.actions.suspend.disabledNoActive')
              : undefined
        }
      >
        {t('admin.tenants.actions.suspend.button')}
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabled={isSoftDeleted || !hasSuspended}
        onClick={() => setDialog('activate')}
        title={
          isSoftDeleted
            ? t('admin.tenants.actions.activate.disabledDeleted')
            : !hasSuspended
              ? t('admin.tenants.actions.activate.disabledNoSuspended')
              : undefined
        }
      >
        {t('admin.tenants.actions.activate.button')}
      </Button>
      <Button
        variant="danger"
        size="sm"
        disabled={isSoftDeleted}
        onClick={() => setDialog('softDelete')}
        title={isSoftDeleted ? t('admin.tenants.actions.softDelete.disabledAlready') : undefined}
      >
        {t('admin.tenants.actions.softDelete.button')}
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabled={!isSoftDeleted}
        onClick={() => setDialog('restore')}
        title={!isSoftDeleted ? t('admin.tenants.actions.restore.disabledNotDeleted') : undefined}
      >
        {t('admin.tenants.actions.restore.button')}
      </Button>
      <Button
        variant="primary"
        size="sm"
        disabled={isSoftDeleted || dbUnavailable}
        onClick={() => setDialog('impersonate')}
        title={
          isSoftDeleted
            ? t('admin.tenants.actions.impersonate.disabledDeleted')
            : dbUnavailable
              ? t('admin.tenants.actions.disabledNoDb')
              : undefined
        }
      >
        {t('admin.tenants.actions.impersonate.button')}
      </Button>
      {canBrowseDb && (
        <Button
          variant="secondary"
          size="sm"
          disabled={isSoftDeleted || dbUnavailable}
          leftIcon={<Database size={14} aria-hidden="true" />}
          onClick={() => navigate(`/tenants/${tenantId}/database`)}
          title={
            isSoftDeleted
              ? t('admin.tenants.actions.dbBrowser.disabledDeleted')
              : dbUnavailable
                ? t('admin.tenants.actions.disabledNoDb')
                : t('admin.tenants.actions.dbBrowser.tooltip')
          }
        >
          {t('admin.tenants.actions.dbBrowser.button')}
        </Button>
      )}
      {/* F8 T8.6 — placeholder, pełna implementacja w F15b (ADR-0016). */}
      <Button
        variant="secondary"
        size="sm"
        disabled
        title={t('admin.tenants.actions.migrate.disabledTooltip')}
      >
        {t('admin.tenants.actions.migrate.button')}
      </Button>

      {dialog === 'suspend' && (
        <ConfirmActionModal
          variant="danger"
          tenantId={tenantId}
          tenantName={tenantName}
          actionKey="suspend"
          endpoint="suspend"
          onClose={close}
          onSuccess={handleSuccess}
        />
      )}
      {dialog === 'activate' && (
        <ConfirmActionModal
          variant="primary"
          tenantId={tenantId}
          tenantName={tenantName}
          actionKey="activate"
          endpoint="activate"
          onClose={close}
          onSuccess={handleSuccess}
        />
      )}
      {dialog === 'softDelete' && (
        <SoftDeleteModal
          tenantId={tenantId}
          tenantName={tenantName}
          onClose={close}
          onSuccess={handleSuccess}
        />
      )}
      {dialog === 'impersonate' && (
        <ImpersonateModal tenantId={tenantId} tenantName={tenantName} onClose={close} />
      )}
      {dialog === 'restore' && (
        <ConfirmActionModal
          variant="primary"
          tenantId={tenantId}
          tenantName={tenantName}
          actionKey="restore"
          endpoint="restore"
          onClose={close}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}

interface ConfirmActionModalProps {
  variant: 'danger' | 'primary';
  tenantId: string;
  tenantName: string;
  actionKey: 'suspend' | 'activate' | 'restore';
  endpoint: 'suspend' | 'activate' | 'restore';
  onClose: () => void;
  onSuccess: () => void;
}

function ConfirmActionModal({
  variant,
  tenantId,
  tenantName,
  actionKey,
  endpoint,
  onClose,
  onSuccess,
}: ConfirmActionModalProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/admin/tenants/${tenantId}/${endpoint}`);
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string }>;
      const code = axErr.response?.data?.error ?? null;
      // 409 z backendu — pokazujemy dedykowany komunikat „już w tym stanie", reszta generic.
      if (axErr.response?.status === 409 && code) {
        setError(t(`admin.tenants.actions.${actionKey}.errors.${code}`));
      } else {
        setError(t(`admin.tenants.actions.${actionKey}.errors.generic`));
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t(`admin.tenants.actions.${actionKey}.title`, { name: tenantName })}
      description={t(`admin.tenants.actions.${actionKey}.description`)}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('admin.tenants.actions.cancel')}
          </Button>
          <Button
            variant={variant}
            onClick={() => void onConfirm()}
            loading={submitting}
            disabled={submitting}
          >
            {submitting
              ? t(`admin.tenants.actions.${actionKey}.submitting`)
              : t(`admin.tenants.actions.${actionKey}.confirm`)}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-tertiary">{t(`admin.tenants.actions.${actionKey}.body`)}</p>
      {error && (
        <div
          role="alert"
          className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
        >
          {error}
        </div>
      )}
    </Modal>
  );
}

interface SoftDeleteModalProps {
  tenantId: string;
  tenantName: string;
  onClose: () => void;
  onSuccess: () => void;
}

function SoftDeleteModal({ tenantId, tenantName, onClose, onSuccess }: SoftDeleteModalProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setReason('');
    setError(null);
    setSubmitting(false);
  }, []);

  const trimmed = reason.trim();
  const tooShort = trimmed.length < 5;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (tooShort) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/admin/tenants/${tenantId}/soft-delete`, { reason: trimmed });
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string }>;
      const code = axErr.response?.data?.error ?? null;
      if (axErr.response?.status === 409 && code === 'ALREADY_DELETED') {
        setError(t('admin.tenants.actions.softDelete.errors.ALREADY_DELETED'));
      } else if (code === 'REASON_TOO_SHORT' || code === 'REASON_TOO_LONG') {
        setError(t(`admin.tenants.actions.softDelete.errors.${code}`));
      } else {
        setError(t('admin.tenants.actions.softDelete.errors.generic'));
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t('admin.tenants.actions.softDelete.title', { name: tenantName })}
      description={t('admin.tenants.actions.softDelete.description')}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('admin.tenants.actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="tenant-soft-delete-form"
            variant="danger"
            loading={submitting}
            disabled={submitting || tooShort}
          >
            {submitting
              ? t('admin.tenants.actions.softDelete.submitting')
              : t('admin.tenants.actions.softDelete.confirm')}
          </Button>
        </>
      }
    >
      <form
        id="tenant-soft-delete-form"
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
        noValidate
      >
        <p className="text-sm text-text-tertiary">{t('admin.tenants.actions.softDelete.body')}</p>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-secondary">
            {t('admin.tenants.actions.softDelete.reasonLabel')}
          </span>
          <textarea
            className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500 dark:border-border-primary dark:bg-bg-tertiary"
            rows={3}
            maxLength={500}
            required
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={submitting}
            placeholder={t('admin.tenants.actions.softDelete.reasonPlaceholder')}
          />
          <span className="text-xs text-text-muted">
            {t('admin.tenants.actions.softDelete.reasonHint', { count: trimmed.length })}
          </span>
        </label>
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
